import {
    queryOptions,
    type UseMutationResult,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import {
    createDataSelector,
    createResourceCollectionSelector,
    createResourceSelector,
    handleJsonApiError,
    injectPageParams,
    type PageParams,
} from "jsonapi-zod-query";
import { z } from "zod";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";

export const ownerAttributesSchema = z.object({
    id: z.number(),
    ownerName: z.string(),
    ownerFirstName: z.string(),
    email: z.string(),
    phone: z.string().optional(),
    phone2: z.string().optional(),
});

const ownerUnit = z.object({
    id: z.number(),
    rentalId: z.string(),
    unitPhone: z.string().optional(),
    rentalAgentPhone: z.string().optional(),
    codedUnit: z.string().optional(),
});

const ownerSelector = createDataSelector(
    createResourceSelector({
        type: "owner",
        attributesSchema: ownerAttributesSchema,
        relationships: {
            calendars: {
                resourceType: "calendar",
                relationshipType: "many",
                include: {
                    type: "calendar",
                    attributesSchema: ownerUnit,
                },
            },
        },
    }),
);

const ownersSelector = createResourceCollectionSelector({
    type: "owner",
    attributesSchema: ownerAttributesSchema,
});

export type Owner = ReturnType<typeof ownerSelector>;
export type PaginatedOwners = ReturnType<typeof ownersSelector>;
export type ListOwners = PaginatedOwners["data"][number];
export type OwnerUnit = z.infer<typeof ownerUnit>;

export type ListOwnersOptions = {
    pageParams?: PageParams;
};

export const createOwnerQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListOwnersOptions) =>
        queryOptions({
            queryKey: ["owners", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/owners");
                injectPageParams(url, options?.pageParams);

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return ownersSelector(await response.json());
            },
        }),
    get: (email: string) =>
        queryOptions({
            queryKey: ["ownerByEmail", email],
            queryFn: async ({ signal }) => {
                const response = await authFetch(apiUrl(`/owners/${email}`), { signal });
                await handleJsonApiError(response);
                return ownerSelector(await response.json());
            },
        }),
});

export const useDisableOwnerMutation = (): UseMutationResult<void, Error, number> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ownerId) => {
            const response = await fetch(apiUrl(`/owners/${ownerId}`), {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
            });
            await handleJsonApiError(response);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["units"],
                refetchType: "all",
            });
        },
    });
};
